<template>
  <div class="default-carrier-form-container">
    <div v-if="isLoading" class="loading-spinner">
      <p><i class="fas fa-spinner fa-spin"></i> 載入載具資料...</p>
    </div>

    <form v-else @submit.prevent="submitForm" class="edit-form">
      <div class="form-group">
        <label for="carrierId">手機條碼 <span class="required">*</span></label>
        <input
          type="text"
          id="carrierId"
          v-model="carrier.carrierId"
          required
          class="form-input"
          placeholder="例：/A012C3V（/ 開頭共 8 碼）"
        >
      </div>

      <div class="form-group checkbox-group">
        <label class="checkbox-label">
          <input type="checkbox" v-model="carrier.enabled">
          啟用（加入隨機挑選池）
        </label>
      </div>

      <div class="form-group">
        <label for="remark">備註</label>
        <input
          type="text"
          id="remark"
          v-model="carrier.remark"
          class="form-input"
          placeholder="選填，例如：載具持有人姓名"
        >
      </div>

      <div class="form-actions">
        <button type="button" class="cancel-btn" @click="handleCancel">
          <i class="fas fa-times"></i> 取消
        </button>
        <button type="submit" class="submit-btn" :disabled="isSubmitting">
          <i v-if="!isSubmitting" class="fas fa-save"></i>
          <i v-else class="fas fa-spinner fa-spin"></i>
          {{ isSubmitting ? '儲存中...' : (isEditMode ? '更新載具' : '新增載具') }}
        </button>
      </div>
    </form>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, defineProps, defineEmits } from 'vue';
import { useToast } from 'vue-toastification';
import {
  createDefaultCarrier,
  updateDefaultCarrier,
  getDefaultCarrierById
} from '@/api/defaultCarrier.api.js';

const props = defineProps({
  carrierData: {
    type: Object,
    default: null
  },
  isEditMode: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(['close', 'saved']);

const toast = useToast();
const isLoading = ref(false);
const isSubmitting = ref(false);

const carrier = reactive({
  carrierId: '',
  enabled: true,
  remark: ''
});

const loadCarrierData = async () => {
  if (props.isEditMode && props.carrierData) {
    isLoading.value = true;
    try {
      const data = await getDefaultCarrierById(props.carrierData.id);
      carrier.carrierId = data.carrierId || '';
      carrier.enabled = data.enabled !== false;
      carrier.remark = data.remark || '';
    } catch (error) {
      toast.error('載入載具資料失敗');
      emit('close');
    } finally {
      isLoading.value = false;
    }
  }
};

const submitForm = async () => {
  if (isSubmitting.value) return;

  const carrierId = carrier.carrierId.trim();
  if (!/^\/[0-9A-Za-z.+-]{7}$/.test(carrierId)) {
    toast.error('手機條碼格式錯誤，需為「/」開頭共 8 碼，例：/A012C3V');
    return;
  }

  isSubmitting.value = true;

  try {
    const submitData = {
      carrierId: carrierId.toUpperCase(),
      enabled: carrier.enabled,
      remark: carrier.remark.trim() || null
    };

    if (props.isEditMode) {
      await updateDefaultCarrier(props.carrierData.id, submitData);
      toast.success('預設載具更新成功！');
    } else {
      await createDefaultCarrier(submitData);
      toast.success('預設載具新增成功！');
    }

    emit('saved');
  } catch (error) {
    const errorMsg = error.response?.data?.message || '操作失敗';
    toast.error(`${props.isEditMode ? '更新' : '新增'}預設載具失敗：${errorMsg}`);
  } finally {
    isSubmitting.value = false;
  }
};

const handleCancel = () => {
  emit('close');
};

onMounted(() => {
  loadCarrierData();
});
</script>

<style scoped>
.default-carrier-form-container {
  background-color: rgba(30, 30, 40, 0.9);
  border: 1px solid rgba(160, 100, 220, 0.3);
  border-radius: 8px;
  padding: 30px;
  box-shadow: 0 4px 15px rgba(0,0,0,0.3);
}

.loading-spinner {
  text-align: center;
  padding: 40px;
  font-size: 1.1em;
  color: #888e95;
}

.edit-form {
  max-width: 600px;
}

.form-group {
  margin-bottom: 25px;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  font-weight: 600;
  color: #e0e0e0;
  font-size: 0.95em;
}

.required {
  color: #ff5555;
}

.form-input {
  width: 100%;
  padding: 12px 15px;
  background-color: rgba(20, 20, 30, 0.8);
  border: 1px solid rgba(160, 100, 220, 0.4);
  border-radius: 5px;
  color: #e0e0e0;
  font-size: 1em;
  box-sizing: border-box;
  transition: all 0.2s ease;
}

.form-input:focus {
  outline: none;
  border-color: #ff8c69;
  box-shadow: 0 0 8px rgba(255, 140, 105, 0.3);
}

.checkbox-group {
  display: flex;
  align-items: center;
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  margin-bottom: 0 !important;
}

.checkbox-label input[type="checkbox"] {
  width: 18px;
  height: 18px;
  cursor: pointer;
}

.form-actions {
  display: flex;
  gap: 15px;
  justify-content: flex-end;
  margin-top: 30px;
  padding-top: 20px;
  border-top: 1px solid rgba(160, 100, 220, 0.2);
}

.cancel-btn, .submit-btn {
  padding: 12px 24px;
  border-radius: 5px;
  cursor: pointer;
  font-weight: 500;
  font-size: 1em;
  transition: all 0.2s ease;
  border: none;
  display: flex;
  align-items: center;
  gap: 8px;
}

.cancel-btn {
  background-color: rgba(80, 80, 90, 0.7);
  color: #c0c0c0;
  border: 1px solid rgba(100,100,110,0.8);
}

.cancel-btn:hover {
  background-color: rgba(100, 100, 110, 0.8);
}

.submit-btn {
  background: linear-gradient(145deg, #ff8c69, #e06c55);
  color: #ffffff;
}

.submit-btn:hover:not(:disabled) {
  background: linear-gradient(145deg, #e06c55, #cc5a47);
}

.submit-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

@media (max-width: 768px) {
  .default-carrier-form-container {
    padding: 20px;
  }

  .form-actions {
    flex-direction: column;
  }

  .cancel-btn, .submit-btn {
    width: 100%;
    justify-content: center;
  }
}
</style>
